import { Colors } from "../../config/DesignConstants";
import { mod, polarToVec } from "../../core/util/MathUtil";
import { rInteger } from "../../core/util/Random";
import { V, V2d } from "../../core/Vector";
import { BaseEnemy } from "./BaseEnemy";
import { InterceptorSwarm } from "./InterceptorSwarm";

//=============================================================================
// CARRIER CONFIGURATION - All tweakable values in one place
//=============================================================================

const CARRIER_CONFIG = {
  // Basic stats
  health: 150, // Lots of health - tough enemy
  killPoints: 100, // High value target
  mass: 5, // Heavy and imposing

  // Visual
  color: Colors.CRIMSON,
  secondaryColor: Colors.DARK_CRIMSON,
  strokeWidth: 4,
  crossThickArmLength: 18,
  crossThinArmLength: 12,

  // Orbital movement
  orbitRadius: 200, // Distance from center for waypoints
  numWaypoints: 6, // Number of orbital waypoints
  waypointReachDistance: 40, // Distance to consider waypoint "reached"

  // Movement speeds
  maxSpeed: 60, // Maximum orbital speed
  acceleration: 30, // Acceleration toward waypoints
  deceleration: 20, // Deceleration when approaching waypoints
  minStopSpeed: 5, // Speed below which we snap to full stop

  // Spawning behavior
  pauseTime: 4.0, // Total time to pause at waypoint
  spawnDelay: 0.5, // Delay into pause before spawning (as fraction)
} as const;

//=============================================================================
// CARRIER ENEMY CLASS - Orbital command ship that spawns interceptor swarms
//=============================================================================

export class CarrierEnemy extends BaseEnemy {
  // Orbital waypoint state
  private waypoints: V2d[] = [];
  private currentWaypointIndex = 0;
  private isPaused = false;
  private pauseTimer = 0;
  private hasSpawnedSwarm = false;

  constructor(position: V2d) {
    const crossShape = CarrierEnemy.createCrossShape();

    super(position, {
      health: CARRIER_CONFIG.health,
      killPoints: CARRIER_CONFIG.killPoints,
      mass: CARRIER_CONFIG.mass,
      visualConfig: {
        points: [...crossShape],
        color: CARRIER_CONFIG.color,
        secondaryColor: CARRIER_CONFIG.secondaryColor,
        strokeWidth: CARRIER_CONFIG.strokeWidth,
      },
    });

    this.generateOrbitalWaypoints();
  }

  //===========================================================================
  // SHAPE GENERATION - Create the cross shape for the carrier
  //===========================================================================

  private static createCrossShape(): V2d[] {
    const thick = CARRIER_CONFIG.crossThickArmLength;
    const thin = CARRIER_CONFIG.crossThinArmLength;

    return [
      V(-thin, -thick), // Top left outer
      V(thin, -thick), // Top right outer
      V(thin, -thin), // Top right inner
      V(thick, -thin), // Right top
      V(thick, thin), // Right bottom
      V(thin, thin), // Bottom right inner
      V(thin, thick), // Bottom right outer
      V(-thin, thick), // Bottom left outer
      V(-thin, thin), // Bottom left inner
      V(-thick, thin), // Left bottom
      V(-thick, -thin), // Left top
      V(-thin, -thin), // Top left inner
    ];
  }

  //===========================================================================
  // MAIN BEHAVIOR - Clear orbital movement with spawning
  //===========================================================================

  onTick(dt: number): void {
    if (!this.body || this.waypoints.length === 0) return;

    this.rotateToFaceDownward();

    if (this.isPaused) {
      this.handlePauseAtWaypoint(dt);
    } else {
      this.moveTowardCurrentWaypoint();
      this.checkWaypointArrival();
    }
  }

  //===========================================================================
  // MOVEMENT LOGIC - Broken into clear, understandable pieces
  //===========================================================================

  private generateOrbitalWaypoints(): void {
    // Generate waypoints in a higher orbit pattern
    for (let i = 0; i < CARRIER_CONFIG.numWaypoints; i++) {
      const angle = (i / CARRIER_CONFIG.numWaypoints) * Math.PI * 2;
      const waypoint = polarToVec(angle, CARRIER_CONFIG.orbitRadius);
      this.waypoints.push(waypoint);
    }

    // Start at a random waypoint index for varied orbital direction
    this.currentWaypointIndex = rInteger(0, this.waypoints.length);
  }

  private rotateToFaceDownward(): void {
    // Always rotate to keep earth "down" relative to the carrier
    const currentPos = V(this.body!.position);
    const toCenter = V(0, 0).sub(currentPos);
    const angleToCenter = Math.atan2(toCenter.y, toCenter.x);
    this.body!.angle = angleToCenter + Math.PI / 2;
  }

  private moveTowardCurrentWaypoint(): void {
    const currentPos = V(this.body!.position);
    const targetWaypoint = this.waypoints[this.currentWaypointIndex];
    const currentVelocity = V(this.body!.velocity);
    const currentSpeed = currentVelocity.magnitude;

    // Calculate desired movement
    const toWaypoint = targetWaypoint.sub(currentPos);
    const desiredDirection = toWaypoint.normalize();
    const desiredVelocity = desiredDirection.mul(CARRIER_CONFIG.maxSpeed);

    // Calculate steering force
    const steeringForce = desiredVelocity.sub(currentVelocity);

    // Apply acceleration (reduced when near max speed for smoother movement)
    const forceMultiplier =
      currentSpeed < CARRIER_CONFIG.maxSpeed * 0.9
        ? CARRIER_CONFIG.acceleration
        : CARRIER_CONFIG.acceleration * 0.3;

    const limitedForce = steeringForce
      .normalize()
      .mul(forceMultiplier * this.body!.mass);

    this.body!.applyForce([limitedForce.x, limitedForce.y]);
  }

  private checkWaypointArrival(): void {
    const currentPos = V(this.body!.position);
    const targetWaypoint = this.waypoints[this.currentWaypointIndex];
    const distanceToWaypoint = targetWaypoint.sub(currentPos).magnitude;

    if (distanceToWaypoint < CARRIER_CONFIG.waypointReachDistance) {
      this.startPauseAtWaypoint();
    }
  }

  private startPauseAtWaypoint(): void {
    this.isPaused = true;
    this.pauseTimer = CARRIER_CONFIG.pauseTime;
    this.hasSpawnedSwarm = false;
  }

  private handlePauseAtWaypoint(dt: number): void {
    this.pauseTimer -= dt;

    // Decelerate to a stop
    this.decelerateToStop();

    // Spawn interceptor swarm partway through the pause
    const spawnTime = CARRIER_CONFIG.pauseTime * CARRIER_CONFIG.spawnDelay;
    if (
      !this.hasSpawnedSwarm &&
      this.pauseTimer <= CARRIER_CONFIG.pauseTime - spawnTime
    ) {
      this.spawnInterceptorSwarm();
      this.hasSpawnedSwarm = true;
    }

    // Move to next waypoint after pause
    if (this.pauseTimer <= 0) {
      this.moveToNextWaypoint();
    }
  }

  private decelerateToStop(): void {
    const currentVelocity = V(this.body!.velocity);
    const currentSpeed = currentVelocity.magnitude;

    if (currentSpeed > 0.1) {
      const decelerationForce = currentVelocity
        .normalize()
        .mul(-CARRIER_CONFIG.deceleration * this.body!.mass);
      this.body!.applyForce([decelerationForce.x, decelerationForce.y]);

      // Snap to full stop when very slow
      if (currentSpeed < CARRIER_CONFIG.minStopSpeed) {
        this.body!.velocity = [0, 0];
      }
    } else {
      this.body!.velocity = [0, 0];
    }
  }

  private moveToNextWaypoint(): void {
    this.currentWaypointIndex = mod(
      this.currentWaypointIndex + 1,
      this.waypoints.length,
    );
    this.isPaused = false;
  }

  //===========================================================================
  // SPAWNING - Simple and clear
  //===========================================================================

  private spawnInterceptorSwarm(): void {
    if (!this.game) return;

    const currentLevel = this.getCurrentLevel();
    if (!currentLevel) return;

    // Spawn interceptor swarm at carrier's position
    const spawnPos = V(this.body!.position);
    const swarm = new InterceptorSwarm(spawnPos);

    currentLevel.addChild(swarm);
  }
}
